import React from 'react';
import {Link, useParams} from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AreasSection from '../components/AreasSection';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiBuilding, FiCheck, FiPhone, FiCalendar, FiStar, FiUser, FiShield, FiClock, FiUsers} = FiIcons;

const OfficeCleaningPage = () => {
  const {area} = useParams();
  const currentArea = area ? formatAreaName(area) : 'Aylesbury';

  const handleLinkClick = () => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const services = [
    {
      title: "Daily Office Maintenance",
      description: "Regular cleaning to maintain a productive and professional work environment for your team.",
      features: [
        "Desk and workstation cleaning",
        "Waste bin emptying",
        "Floor vacuuming/mopping",
        "Surface dusting and sanitizing"
      ]
    },
    {
      title: "Washroom & Kitchen Facilities",
      description: "Thorough sanitization of high-use areas to maintain hygiene standards and employee wellbeing.",
      features: [
        "Toilet and washbasin cleaning",
        "Kitchen appliance maintenance",
        "Sink and tap sanitization",
        "Consumable restocking"
      ]
    },
    {
      title: "Meeting Rooms & Reception",
      description: "Specialized cleaning for client-facing areas ensuring your business always makes the best impression.",
      features: [
        "Conference table polishing",
        "Chair cleaning and arrangement",
        "Presentation equipment dusting",
        "Reception area maintenance"
      ]
    },
    {
      title: "Deep Cleaning Services",
      description: "Comprehensive periodic cleaning to maintain the highest standards throughout your office space.",
      features: [
        "Carpet deep cleaning",
        "Window cleaning (internal)",
        "Light fixture cleaning",
        "Detailed furniture cleaning"
      ]
    }
  ];

  const benefits = [
    "Improved employee productivity and morale",
    "Professional image for clients and visitors",
    "Reduced sick days through better hygiene",
    "Extended lifespan of office furniture and equipment",
    "Compliance with health and safety regulations",
    "Flexible scheduling to minimize business disruption"
  ];

  const reviews = [
    {
      name: "James Mitchell",
      company: "TechStart Solutions Ltd",
      rating: 5,
      text: `Our office in ${currentArea} has never looked better! Diamond Cleaning's team works around our schedule perfectly and maintains exceptional standards every day.`,
      date: "1 week ago"
    },
    {
      name: "Sarah Johnson",
      company: "Buckingham Legal Services",
      rating: 5,
      text: `Professional, reliable, and thorough. Our clients always comment on how clean and welcoming our office environment is. Highly recommend their services in ${currentArea}.`,
      date: "2 weeks ago"
    },
    {
      name: "Michael Chen",
      company: "Creative Design Agency",
      rating: 5,
      text: `The team understands our creative workspace needs perfectly. They maintain our office beautifully while respecting our equipment and ongoing projects.`,
      date: "3 weeks ago"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header currentArea={currentArea} />

      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden pt-20">
        <div className="absolute inset-0 bg-cover bg-center" 
             style={{backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('/img/commerial-homepage.webp')`}} />
        
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Office Cleaning Services in {currentArea}
            </h1>
            
            <p className="text-xl sm:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl mx-auto">
              Professional office cleaning that creates a productive, healthy, and impressive workspace. Daily maintenance, deep cleaning, and specialized services for businesses of all sizes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/book" 
                className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
                onClick={handleLinkClick}
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                <span>Get Quote Online</span>
              </Link>
              <a 
                href="tel:07878522378"
                className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>Call: 07878 522378</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Office Cleaning */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Professional Office Cleaning in {currentArea}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                A clean office environment is essential for productivity, employee wellbeing, and making the right impression on clients. Our professional office cleaning services ensure your workspace remains spotless, hygienic, and welcoming throughout the working week.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                We understand that every business has unique requirements and schedules. Our flexible cleaning services work around your operations, whether you need daily maintenance, evening cleaning, or weekend deep cleans. We're equipped to handle offices of all sizes across {currentArea}.
              </p>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900">Benefits of Professional Office Cleaning</h3>
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl h-96 bg-cover bg-center" 
                   style={{backgroundImage: `url('/img/commerial-homepage.webp')`}}>
                <div className="absolute inset-0 bg-blue-600 bg-opacity-20"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Office Cleaning Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive cleaning solutions tailored to modern office environments
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {service.description}
                </p>
                <div className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-600" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Enhance Your Office Environment?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Professional office cleaning that boosts productivity and impresses clients in {currentArea}.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 rounded-xl p-6">
              <SafeIcon icon={FiClock} className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Flexible Timing</h3>
              <p className="text-blue-100">Evening, morning, or weekend cleaning</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <SafeIcon icon={FiShield} className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Fully Insured</h3>
              <p className="text-blue-100">Complete business protection</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <SafeIcon icon={FiUsers} className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Trained Team</h3>
              <p className="text-blue-100">Professional office cleaning specialists</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/book" 
              className="bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
              onClick={handleLinkClick}
            >
              <SafeIcon icon={FiCalendar} className="w-5 h-5" />
              <span>Get Free Quote</span>
            </Link>
            <a 
              href="tel:07878522378"
              className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span>Call: 07878 522378</span>
            </a>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Business Owners Say About Our Office Cleaning
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real feedback from satisfied business clients in {currentArea}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <SafeIcon icon={FiUser} className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{review.name}</h4>
                    <p className="text-sm text-gray-600">{review.company}</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 mr-2">
                    {[...Array(review.rating)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">{review.date}</span>
                </div>
                <p className="text-gray-700 leading-relaxed">"{review.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Section */}
      <AreasSection currentArea={currentArea} />
      <Footer />
    </div>
  );
};

const formatAreaName = (area) => {
  return area
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default OfficeCleaningPage;